/**
 * Invoke a Cloud Function callable, loading the Firebase SDK only at call time.
 *
 * Every wrapper in this directory used to bind its callable at module scope:
 *
 *     const submitFn = httpsCallable(functions, 'submitInquiry');
 *
 * That one line makes the module a static dependent of `firebase/functions` and
 * `../firebase.js`. The schemas beside it are plain zod and the forms need them
 * to validate as a visitor types, so these modules are legitimately on the
 * critical path — and they dragged the whole Functions client onto it with them.
 * Measured 2026-08-23: `/contact`, `/signup` and `/help` each preloaded a 354 KB
 * Firebase chunk at the highest priority, to send a form the visitor had not
 * filled in yet.
 *
 * Binding at call time costs one dynamic import on submit — after a click, where
 * a few ms is invisible — and takes the SDK off first paint entirely.
 *
 * Returns the callable's payload directly; callers validate it against their own
 * output schema, exactly as before.
 */
export async function callFunction<TInput, TOutput>(name: string, input: TInput): Promise<TOutput> {
  const [{ httpsCallable }, { functions }] = await Promise.all([
    import('firebase/functions'),
    import('../firebase.js'),
  ]);
  const fn = httpsCallable<TInput, TOutput>(functions, name);
  const result = await fn(input);
  return result.data;
}
